import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import { ConnectAgentCard } from '../components/ConnectAgentCard';
import {
  useAgentKeys,
  useMintAgentKey,
  useRevokeAgentKey,
  useToolRegistry,
  useUpdateAgentKey,
} from '../hooks/useOps';
import type { AgentKey, MintedKey } from '../api/types';
import { formatDate } from '../lib/format';

function keyStatus(k: AgentKey): { label: string; color: 'success' | 'error' | 'default' } {
  if (k.revokedAt) return { label: 'Revoked', color: 'error' };
  if (!k.enabled) return { label: 'Disabled', color: 'default' };
  return { label: 'Active', color: 'success' };
}

export function AgentAccessPage() {
  const { data: keys = [], isLoading, error } = useAgentKeys();
  const { data: registry } = useToolRegistry();
  const mint = useMintAgentKey();
  const update = useUpdateAgentKey();
  const revoke = useRevokeAgentKey();

  // Tool options come from the live registry so the picker never drifts.
  const allTools = (registry?.tools ?? []).map((t) => t.name);

  const [minting, setMinting] = useState(false);
  const [name, setName] = useState('');
  const [tiers, setTiers] = useState<string[]>(['read', 'auto_write']);
  const [tools, setTools] = useState<string[]>([]);
  const [minted, setMinted] = useState<MintedKey | null>(null);
  // Persists after the reveal dialog closes so the Connect card stays prefilled.
  const [lastMinted, setLastMinted] = useState<MintedKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);

  const revokedCount = keys.filter((k) => k.revokedAt).length;
  const visibleKeys = showRevoked ? keys : keys.filter((k) => !k.revokedAt);

  const resetForm = () => {
    setName('');
    setTiers(['read', 'auto_write']);
    setTools([]);
    setFormError(null);
  };

  const toggleTier = (t: string) =>
    setTiers((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const submitMint = async () => {
    setFormError(null);
    try {
      const result = await mint.mutateAsync({
        name: name.trim(),
        allowedTiers: tiers,
        allowedTools: tools,
      });
      setMinting(false);
      resetForm();
      setMinted(result);
      setLastMinted(result);
      setCopied(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const copyKey = async () => {
    if (!minted) return;
    await navigator.clipboard.writeText(minted.key);
    setCopied(true);
  };

  const toggleEnabled = (k: AgentKey) =>
    update.mutate({ id: k.id, body: { enabled: !k.enabled } });

  const doRevoke = (k: AgentKey) => {
    if (!window.confirm(`Revoke "${k.name}"? This cuts off the agent immediately and can't be undone.`))
      return;
    revoke.mutate(k.id);
  };

  const columns: GridColDef<AgentKey>[] = [
    { field: 'name', headerName: 'Name', minWidth: 180, flex: 1 },
    { field: 'keyPrefix', headerName: 'Key', width: 150, valueGetter: (v) => `${v}…` },
    {
      field: 'allowedTiers',
      headerName: 'Tiers',
      width: 170,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
          {(p.value as string[]).map((t) => (
            <Chip key={t} size="small" label={t} variant="outlined" />
          ))}
        </Stack>
      ),
    },
    {
      field: 'allowedTools',
      headerName: 'Tools',
      minWidth: 150,
      flex: 1,
      sortable: false,
      valueGetter: (v) =>
        (v as string[]).length === 0 ? 'All (within tiers)' : (v as string[]).join(', '),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      sortable: false,
      valueGetter: (_v, row) => keyStatus(row).label,
      renderCell: (p) => {
        const s = keyStatus(p.row);
        return <Chip size="small" label={s.label} color={s.color} variant="outlined" />;
      },
    },
    {
      field: 'lastUsedAt',
      headerName: 'Last used',
      width: 130,
      valueGetter: (v) => (v ? formatDate(v as string) : 'Never'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 190,
      sortable: false,
      renderCell: (p) =>
        p.row.revokedAt ? null : (
          <Stack direction="row" gap={0.5}>
            <Button size="small" onClick={() => toggleEnabled(p.row)}>
              {p.row.enabled ? 'Disable' : 'Enable'}
            </Button>
            <Tooltip title="Revoke (instant cut-off)">
              <Button size="small" color="error" onClick={() => doRevoke(p.row)} startIcon={<BlockIcon />}>
                Revoke
              </Button>
            </Tooltip>
          </Stack>
        ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Agent access"
        action={
          <Stack direction="row" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showRevoked}
                  onChange={(e) => setShowRevoked(e.target.checked)}
                />
              }
              label={`Show revoked${revokedCount ? ` (${revokedCount})` : ''}`}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setMinting(true)}>
              Generate key
            </Button>
          </Stack>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Each key is least-privilege: it can use only the tiers and tools you grant. The kill-switch
        (banner above) halts <em>all</em> agent mutations at once; revoking a key cuts off just that
        agent — both take effect immediately.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ height: 560 }}>
        <DataGrid
          rows={visibleKeys}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </Paper>

      <ConnectAgentCard key={lastMinted?.id ?? 'default'} keys={keys} minted={lastMinted} />

      {/* Mint dialog */}
      <Dialog open={minting} onClose={() => setMinting(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate a scoped agent key</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Name"
            placeholder="hermes-progress-bot"
            helperText="Lowercase kebab-case (letters, digits, hyphens)."
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Typography variant="subtitle2" gutterBottom>
            Tiers
          </Typography>
          <FormGroup row sx={{ mb: 2 }}>
            {['read', 'auto_write'].map((t) => (
              <FormControlLabel
                key={t}
                control={<Checkbox checked={tiers.includes(t)} onChange={() => toggleTier(t)} />}
                label={t}
              />
            ))}
          </FormGroup>
          <Typography variant="caption" color="text.secondary">
            approval_gated is always allowed (it only queues, never executes).
          </Typography>
          <Autocomplete
            multiple
            options={allTools}
            value={tools}
            onChange={(_e, v) => setTools(v)}
            sx={{ mt: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tools (optional)"
                helperText="Leave empty to allow every tool within the chosen tiers."
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMinting(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={mint.isPending || name.trim().length < 3 || tiers.length === 0}
            onClick={submitMint}
          >
            Generate key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reveal-once dialog */}
      <Dialog open={minted !== null} onClose={() => setMinted(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Key generated — copy it now</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This is the only time the full key is shown. Store it in the agent's config; we keep only
            a hash and can't recover it.
          </DialogContentText>
          <TextField
            fullWidth
            value={minted?.key ?? ''}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={copyKey} edge="end" aria-label="copy key">
                      <ContentCopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          {copied && (
            <Typography variant="caption" color="success.main">
              Copied to clipboard.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setMinted(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
